import React, { useState, useEffect } from 'react';
import { Modal, Button, Input, InputNumber, Table, message, Spin, Space, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, SaveOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import './BoxContentModal.css';
import {
  getBoxContentItems,
  createBoxContentItem,
  updateBoxContentItem,
  deleteBoxContentItem
} from '../../../../data/API';

const BoxContentModal = ({ visible, onClose, boxNumber, inventoryId }) => {
  // State management
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [items, setItems] = useState([]);
  const [originalItems, setOriginalItems] = useState([]);
  const [newItemName, setNewItemName] = useState('');
  const [newItemQuantity, setNewItemQuantity] = useState(1);
  const [showAddForm, setShowAddForm] = useState(false);

  // Fetch items when modal opens
  useEffect(() => {
    if (visible && inventoryId) {
      fetchItems();
    }
  }, [visible, inventoryId]);

  // Fetch box content items
  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await getBoxContentItems(inventoryId);

      if (response.data) {
        // Handle paginated response format {count, results} or direct array
        const itemsData = response.data.results || response.data;
        const itemsArray = Array.isArray(itemsData) ? itemsData : [];
        setItems(itemsArray);
        setOriginalItems(JSON.parse(JSON.stringify(itemsArray))); // Deep copy
      } else {
        setItems([]);
        setOriginalItems([]);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      console.error('Error response:', error.response);
      message.error('Failed to load box content. Please try again.');
      setItems([]);
      setOriginalItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle quantity change
  const handleQuantityChange = async (id, newQuantity) => {
    // If quantity reaches 0, remove the item
    if (newQuantity === 0) {
      const itemToRemove = items.find(item => item.id === id || item.tempId === id);
      if (itemToRemove) {
        await handleDeleteItem(itemToRemove);
      }
      return;
    }

    // Otherwise, update the quantity
    setItems(items.map(item => {
      // Match by id for existing items or tempId for new items
      const matches = item.id === id || item.tempId === id;
      return matches ? { ...item, quantity: newQuantity, modified: true } : item;
    }));
  };

  // Handle name change
  const handleNameChange = (id, newName) => {
    setItems(items.map(item => {
      // Match by id for existing items or tempId for new items
      const matches = item.id === id || item.tempId === id;
      return matches ? { ...item, name: newName, modified: true } : item;
    }));
  };

  // Add new item to list (not saved yet)
  const handleAddItem = () => {
    if (!newItemName.trim()) {
      message.error('Please enter item description');
      return;
    }

    const newItem = {
      tempId: `temp_${Date.now()}`, // Temporary ID for new items
      name: newItemName.trim(),
      quantity: newItemQuantity,
      storage_inventory: inventoryId,
      is_active: true,
      isNew: true // Flag to identify new items
    };

    setItems([...items, newItem]);
    setNewItemName('');
    setNewItemQuantity(1);
    setShowAddForm(false);
  };

  // Remove item from list (soft delete existing, remove new)
  const handleDeleteItem = async (item, silent = false) => {
    if (item.id) {
      // Existing item - soft delete via API immediately
      try {
        await deleteBoxContentItem(item.id);
        setItems(items.filter(i => i.id !== item.id));
        if (!silent) {
          message.success('Item removed');
        }
      } catch (error) {
        console.error('Delete error:', error);
        message.error('Failed to remove item');
      }
    } else {
      // New item not yet saved - just remove from state
      setItems(items.filter(i => i.tempId !== item.tempId));
      if (!silent) {
        message.success('Item removed');
      }
    }
  };

  // Save all changes
  const handleSave = async () => {
    setSaving(true);
    try {
      // Validate all items
      const invalidItem = items.find(item => !item.name.trim() || item.quantity < 1);
      if (invalidItem) {
        message.error('All items must have a description and quantity of at least 1');
        setSaving(false);
        return;
      }

      // Process new items
      const newItems = items.filter(item => item.isNew);
      for (const item of newItems) {
        await createBoxContentItem(item.name, item.quantity, inventoryId);
      }

      // Process modified existing items
      const modifiedItems = items.filter(item => item.modified && item.id);
      for (const item of modifiedItems) {
        await updateBoxContentItem(item.id, item.name, item.quantity, inventoryId);
      }

      message.success('Box content saved successfully!');
      setEditMode(false);
      setShowAddForm(false);
      fetchItems(); // Refresh to get updated data with new IDs
    } catch (error) {
      console.error('Save error:', error);
      message.error('Failed to save box content. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Toggle edit mode
  const handleToggleEditMode = () => {
    if (editMode) {
      // Exiting edit mode - restore original items
      setItems(JSON.parse(JSON.stringify(originalItems)));
      setShowAddForm(false);
    }
    setEditMode(!editMode);
  };

  // Handle modal close
  const handleClose = () => {
    if (editMode && (items.some(i => i.isNew || i.modified))) {
      Modal.confirm({
        title: 'Unsaved Changes',
        content: 'You have unsaved changes. Are you sure you want to close?',
        onOk: () => {
          setEditMode(false);
          setShowAddForm(false);
          setItems(JSON.parse(JSON.stringify(originalItems)));
          onClose();
        }
      });
    } else {
      setEditMode(false);
      setShowAddForm(false);
      onClose();
    }
  };

  // Table columns for view mode
  const viewColumns = [
    {
      title: 'Description',
      dataIndex: 'name',
      key: 'name',
      width: '70%',
    },
    {
      title: 'Qt.',
      dataIndex: 'quantity',
      key: 'quantity',
      width: '30%',
      align: 'center',
    },
  ];

  // Table columns for edit mode
  const editColumns = [
    {
      title: 'Description',
      dataIndex: 'name',
      key: 'name',
      width: '50%',
      render: (text, record) => (
        <Input
          value={text}
          onChange={(e) => handleNameChange(record.id || record.tempId, e.target.value)}
          placeholder="Item description"
        />
      ),
    },
    {
      title: 'Qt.',
      dataIndex: 'quantity',
      key: 'quantity',
      width: '35%',
      align: 'center',
      render: (quantity, record) => (
        <div className='quantity-control-custom-wrapper'>
          <div className="quantity-control quantity-control-custom-inline">
            <Button
              size="small"
              onClick={() => handleQuantityChange(record.id || record.tempId, quantity - 1)}
            >
              -
            </Button>
            <InputNumber
              min={1}
              max={999}
              value={quantity}
              onChange={(value) => handleQuantityChange(record.id || record.tempId, value)}
              className="quantity-input"
              controls={false}
            />
            <Button
              size="small"
              onClick={() => handleQuantityChange(record.id || record.tempId, Math.min(999, quantity + 1))}
              disabled={quantity >= 999}
            >
              +
            </Button>
          </div>

          <Popconfirm
            title="Remove this item?"
            onConfirm={() => handleDeleteItem(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </div>
      ),
    },
//     {
//       title: '',
//       key: 'action',
//       width: '15%',
//       align: 'center',
//       className: 'custom-delete-col',
//       render: (_, record) => (
// <></>
//       ),
//     },
  ];

  return (
    <Modal
    className='box-content-custom-wrapper'
      title={
        <div className="box-content-modal-header">
          <h3 className="mb-0">Box content</h3>
          <p className="text-muted small mb-0">
            {editMode ? 'Add or edit your box content here' : `Box ${boxNumber}`}
          </p>
        </div>
      }
      open={visible}
      onCancel={handleClose}
      width={450}
      centered
      footer={[
        <Space key="footer-buttons" size="middle" className="modal-footer-buttons">
          {!editMode ? (
            <>
              {/* <Button key="close" onClick={handleClose}>
                Close
              </Button> */}
              <Button
                key="edit"
                type="primary"
                icon={<EditOutlined />}
                onClick={handleToggleEditMode}
                className="box-content-edit-btn"
              >
                Edit content
              </Button>
            </>
          ) : (
            <>
              {/* <Button
                key="cancel"
                onClick={handleToggleEditMode}
                disabled={saving}
              >
                Cancel
              </Button> */}
              <Button
                key="save"
                type="primary"
                danger
                onClick={handleSave}
                loading={saving}
                className="box-content-save-btn"
              >
                Save content
              </Button>
            </>
          )}
        </Space>
      ]}
    >
      <Spin spinning={loading} tip="Loading box content...">
        <div className="box-content-container">
          {items.length === 0 && !editMode ? (
            <div className="empty-state">
              <p className="text-muted">No items added yet.</p>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleToggleEditMode}
              >
                Add Items
              </Button>
            </div>
          ) : (
            <div className='content-table'>
            <Table
              dataSource={items}
              columns={editMode ? editColumns : viewColumns}
              pagination={false}
              rowKey={(record) => record.id || record.tempId}
              size="small"
              className={`box-content-table box-content-table-custom ${editMode ? 'editTable' : 'viewTable' }`}
            />
            </div>
          )}

          {editMode && (
            <div className="add-item-section">
              {!showAddForm ? (
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => setShowAddForm(true)}
                  block
                  className="add-item-button"
                >
                  Add Item
                </Button>
              ) : (
                <div className="add-item-form">
                  <Space.Compact style={{ width: '100%' }}>
                    <Input
                      placeholder="Item description"
                      value={newItemName}
                      onChange={(e) => setNewItemName(e.target.value)}
                      onPressEnter={handleAddItem}
                      style={{ width: '55%' }}
                    />
                    <div className='quantity-control-custom-container'>
                      <div className="quantity-control-inline quantity-control-custom-inline">
                        <Button
                          size="small"
                          onClick={() => {
                            if (newItemQuantity === 1) {
                              // Cancel add item form when quantity reaches 0
                              setShowAddForm(false);
                              setNewItemName('');
                              setNewItemQuantity(1);
                            } else {
                              setNewItemQuantity(newItemQuantity - 1);
                            }
                          }}
                        >
                          -
                        </Button>
                        <InputNumber
                          min={1}
                          max={999}
                          value={newItemQuantity}
                          onChange={setNewItemQuantity}
                          style={{ width: '60px' }}
                          controls={false}
                        />
                        <Button
                          size="small"
                          onClick={() => setNewItemQuantity(Math.min(999, newItemQuantity + 1))}
                          disabled={newItemQuantity >= 999}
                        >
                          +
                        </Button>
                      </div>
                      <Button
                        type="primary"
                        className='quantity-control-custom-add-btn'
                        onClick={handleAddItem}
                      >
                        Save item
                    </Button>
                    </div>
                  </Space.Compact>
                </div>
              )}
            </div>
          )}
        </div>
      </Spin>
    </Modal>
  );
};

BoxContentModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  boxNumber: PropTypes.string.isRequired,
  inventoryId: PropTypes.number.isRequired
};

export default BoxContentModal;
